import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Edit, Archive, Download, Eye, EyeOff, Trash2, Save, Upload } from "lucide-react";
import api, { API, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/App";
import { LogoMark } from "@/components/Logo";
import { MANDATE_STATUSES, ENQUIRY_STATUSES, COUNTRIES, STAT_SOURCES } from "@/data/constants";
import OpportunityForm from "@/pages/admin/OpportunityForm";

const TABS = ["Opportunities", "Mandates", "CRM", "Categories", "Banner", "Content", "Settings"];
const inputCls = "w-full border border-gold/20 bg-obsidian px-3 py-2 font-sans text-sm text-warmwhite outline-none focus:border-gold";
const label = "font-sans text-[11px] tracking-luxe uppercase text-softgrey/70";

function StatusBadge({ status }) {
  return <span className="border border-gold/30 px-2 py-0.5 font-mono text-[10px] text-champagne">{status}</span>;
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Opportunities");

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-dashboard">
      <header className="flex items-center justify-between border-b border-gold/15 px-6 py-4 lg:px-10">
        <LogoMark />
        <div className="flex items-center gap-5">
          <span className="hidden font-sans text-xs text-softgrey sm:block">{user?.email}</span>
          <button onClick={() => navigate("/")} className="font-sans text-xs text-softgrey hover:text-warmwhite">View Site</button>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout" className="flex items-center gap-2 font-sans text-xs text-softgrey hover:text-warmwhite"><LogOut size={14} /> Logout</button>
        </div>
      </header>

      <div className="flex gap-1 border-b border-gold/10 px-6 lg:px-10">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
                  className={`border-b-2 px-5 py-4 font-sans text-sm transition-colors ${tab === t ? "border-gold text-warmwhite" : "border-transparent text-softgrey hover:text-warmwhite"}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="p-6 lg:p-10">
        {tab === "Opportunities" && <OpportunitiesTab />}
        {tab === "Mandates" && <MandatesTab />}
        {tab === "CRM" && <CRMTab />}
        {tab === "Categories" && <CategoriesTab />}
        {tab === "Banner" && <BannerTab />}
        {tab === "Content" && <ContentTab />}
        {tab === "Settings" && <SettingsTab />}
      </div>
    </div>
  );
}

function OpportunitiesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/opportunities", { params: { include_archived: true } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const archive = async (id) => { await api.delete(`/opportunities/${id}`); toast.success("Archived"); load(); };
  const del = async (id) => { if (!window.confirm("Permanently delete this opportunity?")) return; await api.delete(`/opportunities/${id}?hard=true`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl text-warmwhite">Opportunities <span className="font-mono text-sm text-softgrey">({items.length})</span></h2>
        <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-opportunity" className="flex items-center gap-2 bg-gold px-5 py-2.5 font-sans text-sm font-medium text-obsidian hover:brightness-110"><Plus size={16} /> Add Opportunity</button>
      </div>

      <div className="mt-6 overflow-x-auto border border-gold/15">
        <table className="w-full min-w-[720px]">
          <thead className="bg-seablue/30">
            <tr className="text-left font-sans text-[10px] tracking-luxe uppercase text-softgrey/70">
              <th className="px-4 py-3">Deal ID</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Fee</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} className="border-t border-gold/10 hover:bg-seablue/10" data-testid={`admin-opp-${o.deal_id}`}>
                <td className="px-4 py-3 font-mono text-xs text-gold">{o.deal_id}</td>
                <td className="max-w-xs truncate px-4 py-3 font-sans text-sm text-warmwhite">{o.title}</td>
                <td className="px-4 py-3 font-sans text-xs text-softgrey">{o.category}</td>
                <td className="px-4 py-3 font-mono text-xs text-champagne">{o.founders_fee}</td>
                <td className="px-4 py-3"><span className={`font-mono text-[10px] ${o.status === "active" ? "text-green-400" : "text-softgrey/50"}`}>{o.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => { setEditing(o); setOpen(true); }} className="flex items-center gap-1.5 border border-gold/40 px-3 py-1.5 font-sans text-xs text-gold hover:bg-gold/10" title="Edit details & images" data-testid={`edit-${o.deal_id}`}><Edit size={13} /> Edit / Images</button>
                    <button onClick={() => archive(o.id)} className="text-softgrey hover:text-champagne" title="Archive"><Archive size={15} /></button>
                    <button onClick={() => del(o.id)} className="text-softgrey hover:text-red-400" title="Delete"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <OpportunityForm initial={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function MandatesTab() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ status: "", source_type: "", country: "" });
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    const p = {}; Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    api.get("/mandates", { params: p }).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [filters]);

  const update = async (id, patch) => {
    const { data } = await api.put(`/mandates/${id}`, patch);
    toast.success("Updated");
    setItems((prev) => prev.map((m) => (m.id === id ? data : m)));
    if (selected?.id === id) setSelected(data);
    setNote("");
  };

  const exportCsv = () => {
    const token = localStorage.getItem("gs_token");
    fetch(`${API}/mandates-export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "mandates.csv"; a.click(); URL.revokeObjectURL(u); });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif text-2xl text-warmwhite">Bespoke Mandates <span className="font-mono text-sm text-softgrey">({items.length})</span></h2>
        <button onClick={exportCsv} data-testid="export-mandates" className="flex items-center gap-2 border border-gold/30 px-4 py-2 font-sans text-sm text-warmwhite hover:bg-gold/10"><Download size={15} className="text-gold" /> Export CSV</button>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={`${inputCls} [&>option]:bg-obsidian`} data-testid="filter-status">
          <option value="">All Statuses</option>{MANDATE_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.source_type} onChange={(e) => setFilters({ ...filters, source_type: e.target.value })} className={`${inputCls} [&>option]:bg-obsidian`}>
          <option value="">All Types</option>{["Property / Real Estate", "Materials", "Equipment", "Commercial Opportunity", "Other"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} className={`${inputCls} [&>option]:bg-obsidian`}>
          <option value="">All Countries</option>{COUNTRIES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="mt-6 overflow-x-auto border border-gold/15">
        <table className="w-full min-w-[760px]">
          <thead className="bg-seablue/30">
            <tr className="text-left font-sans text-[10px] tracking-luxe uppercase text-softgrey/70">
              <th className="px-4 py-3">Ref</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Country</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} className="border-t border-gold/10 hover:bg-seablue/10" data-testid={`mandate-${m.ref}`}>
                <td className="px-4 py-3 font-mono text-xs text-gold">{m.ref}</td>
                <td className="px-4 py-3 font-sans text-sm text-warmwhite">{m.contact?.full_name}</td>
                <td className="px-4 py-3 font-sans text-xs text-softgrey">{m.source_type}</td>
                <td className="px-4 py-3 font-sans text-xs text-softgrey">{m.contact?.country || m.details?.country || m.details?.market || m.details?.delivery_country || "—"}</td>
                <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                <td className="px-4 py-3 text-right"><button onClick={() => setSelected(m)} className="font-sans text-xs text-gold hover:underline" data-testid={`view-mandate-${m.ref}`}>View</button></td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={6} className="px-4 py-10 text-center font-sans text-sm text-softgrey">No mandates yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[70] flex justify-end bg-obsidian/70 backdrop-blur-sm">
          <div className="h-full w-full max-w-lg overflow-y-auto border-l border-gold/20 bg-obsidian p-8" data-testid="mandate-detail">
            <div className="flex items-center justify-between">
              <div><h3 className="font-serif text-2xl text-warmwhite">{selected.contact?.full_name}</h3><p className="font-mono text-xs text-gold">{selected.ref}</p></div>
              <button onClick={() => setSelected(null)} className="text-softgrey hover:text-warmwhite">✕</button>
            </div>

            <div className="mt-6 space-y-2 border-b border-gold/10 pb-6 font-sans text-sm text-softgrey">
              <div>Email: <span className="text-warmwhite">{selected.contact?.email}</span></div>
              <div>Phone: <span className="text-warmwhite">{selected.contact?.phone}</span></div>
              <div>Profile: <span className="text-warmwhite">{selected.contact?.profile_type} {selected.contact?.company_name}</span></div>
              <div>Type: <span className="text-warmwhite">{selected.source_type}</span></div>
              <div>Lead Source: <span className="text-warmwhite">{selected.lead_source}</span></div>
            </div>

            <div className="mt-6">
              <div className={label}>Requirements</div>
              <div className="mt-3 space-y-1.5">
                {Object.entries(selected.details || {}).filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="font-sans text-sm"><span className="text-softgrey">{k.replace(/_/g, " ")}:</span> <span className="text-warmwhite">{v}</span></div>
                ))}
              </div>
              {selected.documents?.length > 0 && (
                <div className="mt-4">
                  <div className={label}>Documents</div>
                  {selected.documents.map((d, i) => <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" className="mt-2 block font-sans text-xs text-gold hover:underline">{d.filename}</a>)}
                </div>
              )}
            </div>

            <div className="mt-6">
              <div className={label}>Update Status</div>
              <select value={selected.status} onChange={(e) => update(selected.id, { status: e.target.value })} className={`${inputCls} mt-2 [&>option]:bg-obsidian`} data-testid="mandate-status-select">
                {MANDATE_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <div className={`${label} mt-4`}>Lead Source</div>
              <input value={selected.lead_source || ""} onChange={(e) => setSelected({ ...selected, lead_source: e.target.value })} onBlur={(e) => update(selected.id, { lead_source: e.target.value })} className={`${inputCls} mt-2`} />
            </div>

            <div className="mt-6">
              <div className={label}>Notes / Follow-ups</div>
              <div className="mt-3 space-y-2">
                {(selected.notes || []).map((n, i) => (
                  <div key={i} className="border border-gold/10 p-3 font-sans text-xs text-softgrey"><span className="text-warmwhite">{n.text}</span><div className="mt-1 text-softgrey/50">{new Date(n.at).toLocaleString()}</div></div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className={inputCls} data-testid="mandate-note-input" />
                <button onClick={() => note && update(selected.id, { note })} className="bg-gold px-4 font-sans text-sm text-obsidian" data-testid="mandate-note-add">Add</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoriesTab() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name.trim()) return; await api.post("/categories", { name }); setName(""); toast.success("Category added"); load(); };
  const save = async (id, val) => { await api.put(`/categories/${id}`, { name: val }); toast.success("Saved"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this category?")) return; await api.delete(`/categories/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="max-w-2xl" data-testid="categories-tab">
      <h2 className="font-serif text-2xl text-warmwhite">Sourcing Categories <span className="font-mono text-sm text-softgrey">({items.length})</span></h2>
      <p className="mt-2 font-sans text-sm text-softgrey">Add categories here and they appear automatically across opportunities and filters — no redesign needed.</p>
      <div className="mt-6 flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New category name" className={inputCls} data-testid="new-category-input" />
        <button onClick={add} className="flex items-center gap-1 bg-gold px-5 font-sans text-sm font-medium text-obsidian" data-testid="add-category"><Plus size={15} /> Add</button>
      </div>
      <div className="mt-6 space-y-2">
        {items.map((c) => (
          <div key={c.id} className="flex items-center gap-2 border border-gold/15 p-3" data-testid={`category-${c.id}`}>
            <input defaultValue={c.name} onBlur={(e) => e.target.value !== c.name && save(c.id, e.target.value)} className={inputCls} />
            <button onClick={() => del(c.id)} className="border border-gold/20 px-3 py-2 text-softgrey hover:text-red-400"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CRMTab() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    api.get("/enquiries").then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, []);

  const counts = ENQUIRY_STATUSES.reduce((acc, st) => {
    acc[st] = items.filter((e) => (e.status || "New") === st).length;
    return acc;
  }, {});
  const filtered = statusFilter ? items.filter((e) => (e.status || "New") === statusFilter) : items;

  const update = async (id, patch) => {
    const { data } = await api.put(`/enquiries/${id}`, patch);
    toast.success("Updated");
    setItems((prev) => prev.map((e) => (e.id === id ? data : e)));
    if (selected?.id === id) setSelected(data);
    setNote("");
  };

  const exportCsv = () => {
    const token = localStorage.getItem("gs_token");
    fetch(`${API}/enquiries-export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "enquiries.csv"; a.click(); URL.revokeObjectURL(u); });
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    await api.delete(`/enquiries/${id}`);
    toast.success("Lead deleted");
    setItems((prev) => prev.filter((e) => e.id !== id));
    setSelected(null);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl text-warmwhite">CRM — Investor Leads <span className="font-mono text-sm text-softgrey">({items.length})</span></h2>
          <p className="mt-1 font-sans text-sm text-softgrey">Track every enquiry through your pipeline with statuses, notes and follow-ups.</p>
        </div>
        <button onClick={exportCsv} data-testid="export-enquiries" className="flex items-center gap-2 border border-gold/30 px-4 py-2 font-sans text-sm text-warmwhite hover:bg-gold/10"><Download size={15} className="text-gold" /> Export CSV</button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded border border-gold/15 bg-gold/15 sm:grid-cols-4 lg:grid-cols-8" data-testid="crm-pipeline-summary">
        <button onClick={() => setStatusFilter("")} className={`bg-obsidian p-4 text-left transition-colors hover:bg-seablue/20 ${statusFilter === "" ? "ring-1 ring-inset ring-gold" : ""}`} data-testid="crm-summary-all">
          <div className="font-serif text-3xl text-gradient-gold">{items.length}</div>
          <div className="mt-1 font-sans text-[9px] tracking-luxe uppercase text-softgrey/70">Total</div>
        </button>
        {ENQUIRY_STATUSES.map((st) => (
          <button key={st} onClick={() => setStatusFilter(st)} className={`bg-obsidian p-4 text-left transition-colors hover:bg-seablue/20 ${statusFilter === st ? "ring-1 ring-inset ring-gold" : ""}`} data-testid={`crm-summary-${st}`}>
            <div className="font-serif text-3xl text-warmwhite">{counts[st]}</div>
            <div className="mt-1 font-sans text-[9px] tracking-luxe uppercase text-softgrey/70">{st}</div>
          </button>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {["", ...ENQUIRY_STATUSES].map((st) => (
          <button key={st || "all"} onClick={() => setStatusFilter(st)} data-testid={`crm-filter-${st || "all"}`}
            className={`border px-4 py-1.5 font-sans text-xs transition-colors ${statusFilter === st ? "border-gold bg-gold text-obsidian" : "border-gold/25 text-softgrey hover:border-gold/60"}`}>
            {st || "All"} <span className="opacity-60">{st ? counts[st] : items.length}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto border border-gold/15">
        <table className="w-full min-w-[760px]">
          <thead className="bg-seablue/30"><tr className="text-left font-sans text-[10px] tracking-luxe uppercase text-softgrey/70">
            <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Opportunity</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id} className="border-t border-gold/10 hover:bg-seablue/10" data-testid={`crm-row-${e.id}`}>
                <td className="px-4 py-3 font-sans text-sm text-warmwhite">{e.name}</td>
                <td className="px-4 py-3 font-sans text-xs text-softgrey">{e.email}</td>
                <td className="max-w-[180px] truncate px-4 py-3 font-sans text-xs text-softgrey">{e.opportunity_title}</td>
                <td className="px-4 py-3"><StatusBadge status={e.status || "New"} /></td>
                <td className="px-4 py-3 font-mono text-[10px] text-softgrey/60">{new Date(e.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => setSelected(e)} className="font-sans text-xs text-gold hover:underline" data-testid={`crm-view-${e.id}`}>Manage</button></td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={6} className="px-4 py-10 text-center font-sans text-sm text-softgrey">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[70] flex justify-end bg-obsidian/70 backdrop-blur-sm">
          <div className="h-full w-full max-w-lg overflow-y-auto border-l border-gold/20 bg-obsidian p-8" data-testid="crm-detail">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl text-warmwhite">{selected.name}</h3>
              <div className="flex items-center gap-4">
                <button onClick={() => del(selected.id)} className="text-softgrey hover:text-red-400" title="Delete lead" data-testid="crm-delete"><Trash2 size={17} /></button>
                <button onClick={() => setSelected(null)} className="text-softgrey hover:text-warmwhite">✕</button>
              </div>
            </div>
            <div className="mt-6 space-y-2 border-b border-gold/10 pb-6 font-sans text-sm text-softgrey">
              <div>Email: <span className="text-warmwhite">{selected.email}</span></div>
              <div>Phone: <span className="text-warmwhite">{selected.phone || "—"}</span></div>
              <div>Opportunity: <span className="text-warmwhite">{selected.opportunity_title || "General"}</span></div>
              <div>Message: <span className="text-warmwhite">{selected.message || "—"}</span></div>
            </div>
            <div className="mt-6">
              <div className={label}>Status</div>
              <select value={selected.status || "New"} onChange={(e) => update(selected.id, { status: e.target.value })} className={`${inputCls} mt-2 [&>option]:bg-obsidian`} data-testid="crm-status-select">
                {ENQUIRY_STATUSES.map((st) => <option key={st}>{st}</option>)}
              </select>
              <div className={`${label} mt-4`}>Lead Source</div>
              <input defaultValue={selected.lead_source || "Website"} onBlur={(e) => e.target.value !== selected.lead_source && update(selected.id, { lead_source: e.target.value })} className={`${inputCls} mt-2`} />
            </div>
            <div className="mt-6">
              <div className={label}>Notes / Follow-ups</div>
              <div className="mt-3 space-y-2">
                {(selected.notes || []).map((n, i) => (
                  <div key={i} className="border border-gold/10 p-3 font-sans text-xs text-softgrey"><span className="text-warmwhite">{n.text}</span><div className="mt-1 text-softgrey/50">{new Date(n.at).toLocaleString()}</div></div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className={inputCls} data-testid="crm-note-input" />
                <button onClick={() => note && update(selected.id, { note })} className="bg-gold px-4 font-sans text-sm text-obsidian" data-testid="crm-note-add">Add</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BannerTab() {
  const { settings, refresh } = useSettings();
  const [b, setB] = useState(null);
  const [subs, setSubs] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings?.banner) setB(JSON.parse(JSON.stringify(settings.banner))); }, [settings]);
  useEffect(() => { api.get("/subscribers").then((r) => setSubs(r.data)).catch(() => {}); }, []);
  if (!b) return <div className="font-serif text-gold">Loading…</div>;
  const set = (k, v) => setB({ ...b, [k]: v });
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", { banner: b }); toast.success("Banner saved"); refresh(); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  return (
    <div className="max-w-3xl space-y-8" data-testid="banner-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl text-warmwhite">Popup Banner</h2>
          <p className="mt-1 font-sans text-sm text-softgrey">Manage the lead-capture popup that appears on your site.</p>
        </div>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-gold px-5 py-2.5 font-sans text-sm font-medium text-obsidian hover:brightness-110 disabled:opacity-60" data-testid="save-banner"><Save size={15} /> {saving ? "Saving…" : "Save Settings"}</button>
      </div>

      <div className="flex items-center justify-between border border-gold/15 p-5">
        <div>
          <div className="font-sans text-sm text-warmwhite">Popup is {b.enabled ? "Live" : "Off"}</div>
          <div className="font-sans text-xs text-softgrey">Visitors will see the popup after the configured delay.</div>
        </div>
        <label className="flex items-center gap-2 font-sans text-xs text-softgrey" data-testid="banner-enabled">
          <input type="checkbox" checked={!!b.enabled} onChange={(e) => set("enabled", e.target.checked)} /> Enabled
        </label>
      </div>

      <section className="border border-gold/15 p-5">
        <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Content</h3>
        <div className="mt-4 space-y-4">
          <div><div className={label}>Headline</div><input value={b.headline || ""} onChange={(e) => set("headline", e.target.value)} className={`${inputCls} mt-2`} data-testid="banner-headline" /></div>
          <div><div className={label}>Supporting Text</div><textarea rows={2} value={b.supporting_text || ""} onChange={(e) => set("supporting_text", e.target.value)} className={`${inputCls} mt-2`} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><div className={label}>CTA Button Text</div><input value={b.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} className={`${inputCls} mt-2`} /></div>
            <div><div className={label}>Badge Text</div><input value={b.badge || ""} onChange={(e) => set("badge", e.target.value)} className={`${inputCls} mt-2`} /></div>
          </div>
        </div>
      </section>

      <section className="border border-gold/15 p-5">
        <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">After Submission</h3>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div><div className={label}>Success Title</div><input value={b.success_title || ""} onChange={(e) => set("success_title", e.target.value)} className={`${inputCls} mt-2`} /></div>
          <div><div className={label}>Success Text</div><input value={b.success_text || ""} onChange={(e) => set("success_text", e.target.value)} className={`${inputCls} mt-2`} /></div>
        </div>
      </section>

      <section className="border border-gold/15 p-5">
        <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Behaviour</h3>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div><div className={label}>Show Delay (seconds)</div><input type="number" value={b.show_delay ?? 5} onChange={(e) => set("show_delay", Number(e.target.value))} className={`${inputCls} mt-2`} /></div>
          <div><div className={label}>Dismiss Duration (days)</div><input type="number" value={b.dismiss_days ?? 7} onChange={(e) => set("dismiss_days", Number(e.target.value))} className={`${inputCls} mt-2`} /></div>
        </div>
      </section>

      <section className="border border-gold/15 p-5" data-testid="subscribers-list">
        <div className="flex items-center justify-between">
          <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Subscribers</h3>
          <span className="font-mono text-xs text-softgrey">{subs.length} total</span>
        </div>
        <div className="mt-4 space-y-2">
          {subs.length === 0 && <p className="py-6 text-center font-sans text-sm text-softgrey/60">No subscribers yet.</p>}
          {subs.map((sub) => (
            <div key={sub.id} className="flex items-center justify-between border-b border-gold/10 py-2 font-sans text-sm">
              <span className="text-warmwhite">{sub.email}</span>
              <span className="font-mono text-[10px] text-softgrey/60">{new Date(sub.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function ContentTab() {
  const { settings, refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings) setS(JSON.parse(JSON.stringify(settings))); }, [settings]);
  if (!s) return <div className="font-serif text-gold">Loading…</div>;
  const c = s.content || {};
  const patch = (fn) => setS((prev) => { const n = JSON.parse(JSON.stringify(prev)); n.content = n.content || {}; fn(n.content); return n; });
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { contact: s.contact, fees_text: s.fees_text, bespoke_deposit: s.bespoke_deposit || "", what_we_source: s.what_we_source, hero: s.hero, sections: s.sections, legal: s.legal || {}, team: s.team || {}, stats: s.stats || [], stats_coming_soon: s.stats_coming_soon || false, stats_eyebrow: s.stats_eyebrow || "", stats_heading: s.stats_heading || "", process: s.process || {}, content: s.content || {}, signature: s.signature || {}, affiliations: s.affiliations || {} });
      toast.success("Content saved"); refresh();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const fld = (l, v, on, area) => (
    <div>
      <div className={label}>{l}</div>
      {area
        ? <textarea rows={2} value={v || ""} onChange={(e) => on(e.target.value)} className={`${inputCls} mt-2`} />
        : <input value={v || ""} onChange={(e) => on(e.target.value)} className={`${inputCls} mt-2`} />}
    </div>
  );
  const sec = (title, children) => (
    <section className="border border-gold/15 p-5">
      <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">{title}</h3>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );

  return (
    <div className="max-w-3xl space-y-8" data-testid="content-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl text-warmwhite">Site Content</h2>
          <p className="mt-1 font-sans text-sm text-softgrey">Edit every headline, step and card shown across the homepage and About.</p>
        </div>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-gold px-5 py-2.5 font-sans text-sm font-medium text-obsidian hover:brightness-110 disabled:opacity-60" data-testid="save-content"><Save size={15} /> {saving ? "Saving…" : "Save Changes"}</button>
      </div>

      {sec("Hero", <>
        {fld("Eyebrow", c.hero_eyebrow, (v) => patch((x) => { x.hero_eyebrow = v; }))}
        {fld("Bottom Strip", c.hero_strip, (v) => patch((x) => { x.hero_strip = v; }))}
      </>)}

      {sec("Section Headings", <>
        <div className="grid grid-cols-2 gap-4">
          {fld("What We Source — Eyebrow", c.wws_eyebrow, (v) => patch((x) => { x.wws_eyebrow = v; }))}
          {fld("What We Source — Heading", c.wws_heading, (v) => patch((x) => { x.wws_heading = v; }))}
          {fld("Featured — Eyebrow", c.featured_eyebrow, (v) => patch((x) => { x.featured_eyebrow = v; }))}
          {fld("Featured — Heading", c.featured_heading, (v) => patch((x) => { x.featured_heading = v; }))}
        </div>
      </>)}

      {sec("How It Works", <>
        {fld("Eyebrow", c.how_it_works?.eyebrow, (v) => patch((x) => { x.how_it_works.eyebrow = v; }))}
        {fld("Heading", c.how_it_works?.heading, (v) => patch((x) => { x.how_it_works.heading = v; }))}
        {(c.how_it_works?.steps || []).map((st, i) => (
          <div key={i} className="grid grid-cols-[60px_1fr] gap-3 border-t border-gold/10 pt-3">
            {fld("No.", st.n, (v) => patch((x) => { x.how_it_works.steps[i].n = v; }))}
            <div className="space-y-2">
              {fld("Title", st.t, (v) => patch((x) => { x.how_it_works.steps[i].t = v; }))}
              {fld("Description", st.d, (v) => patch((x) => { x.how_it_works.steps[i].d = v; }), true)}
            </div>
          </div>
        ))}
      </>)}

      {sec("Why Goldstone", <>
        {fld("Eyebrow", c.why?.eyebrow, (v) => patch((x) => { x.why.eyebrow = v; }))}
        {fld("Heading", c.why?.heading, (v) => patch((x) => { x.why.heading = v; }))}
        {(c.why?.cards || []).map((cd, i) => (
          <div key={i} className="grid grid-cols-[140px_1fr] gap-3 border-t border-gold/10 pt-3">
            {fld("Icon (lucide)", cd.icon, (v) => patch((x) => { x.why.cards[i].icon = v; }))}
            <div className="space-y-2">
              {fld("Title", cd.t, (v) => patch((x) => { x.why.cards[i].t = v; }))}
              {fld("Description", cd.d, (v) => patch((x) => { x.why.cards[i].d = v; }), true)}
            </div>
            <button onClick={() => patch((x) => { x.why.cards.splice(i, 1); })} className="col-span-2 flex items-center gap-1 font-sans text-xs text-softgrey hover:text-red-400"><Trash2 size={12} /> Remove card</button>
          </div>
        ))}
        <button onClick={() => patch((x) => { (x.why.cards = x.why.cards || []).push({ icon: "Diamond", t: "New", d: "" }); })} className="flex items-center gap-1 font-sans text-xs text-gold"><Plus size={13} /> Add card</button>
      </>)}

      {sec("Global Reach", <>
        {fld("Eyebrow", c.global_reach?.eyebrow, (v) => patch((x) => { x.global_reach.eyebrow = v; }))}
        <div className="grid grid-cols-2 gap-4">
          {fld("Heading Line 1", c.global_reach?.heading_line1, (v) => patch((x) => { x.global_reach.heading_line1 = v; }))}
          {fld("Heading Line 2", c.global_reach?.heading_line2, (v) => patch((x) => { x.global_reach.heading_line2 = v; }))}
        </div>
        {fld("Body", c.global_reach?.body, (v) => patch((x) => { x.global_reach.body = v; }), true)}
      </>)}

      {sec("Footprint Regions", <>
        {(c.footprint_regions || []).map((r, i) => (
          <div key={i} className="flex gap-2">
            <input value={r} onChange={(e) => patch((x) => { x.footprint_regions[i] = e.target.value; })} className={inputCls} />
            <button onClick={() => patch((x) => { x.footprint_regions.splice(i, 1); })} className="border border-gold/20 px-2 text-softgrey hover:text-red-400"><Trash2 size={14} /></button>
          </div>
        ))}
        <button onClick={() => patch((x) => { (x.footprint_regions = x.footprint_regions || []).push("New"); })} className="flex items-center gap-1 font-sans text-xs text-gold"><Plus size={13} /> Add region</button>
      </>)}

      {sec("Final CTA", <>
        <div className="grid grid-cols-2 gap-4">
          {fld("Heading (line 1)", c.final_cta?.heading1, (v) => patch((x) => { x.final_cta.heading1 = v; }))}
          {fld("Heading (accent)", c.final_cta?.heading2, (v) => patch((x) => { x.final_cta.heading2 = v; }))}
        </div>
        {fld("Body", c.final_cta?.body, (v) => patch((x) => { x.final_cta.body = v; }), true)}
      </>)}

      {sec("About Page", <>
        {fld("Eyebrow", c.about?.eyebrow, (v) => patch((x) => { x.about.eyebrow = v; }))}
        {fld("Heading", c.about?.heading, (v) => patch((x) => { x.about.heading = v; }))}
        <div className={label}>Paragraphs</div>
        {(c.about?.paragraphs || []).map((p, i) => (
          <div key={i} className="flex gap-2">
            <textarea rows={2} value={p} onChange={(e) => patch((x) => { x.about.paragraphs[i] = e.target.value; })} className={inputCls} />
            <button onClick={() => patch((x) => { x.about.paragraphs.splice(i, 1); })} className="border border-gold/20 px-2 text-softgrey hover:text-red-400"><Trash2 size={14} /></button>
          </div>
        ))}
        <button onClick={() => patch((x) => { (x.about.paragraphs = x.about.paragraphs || []).push(""); })} className="flex items-center gap-1 font-sans text-xs text-gold"><Plus size={13} /> Add paragraph</button>
        <div className={label}>Highlights</div>
        {(c.about?.highlights || []).map((h, i) => (
          <div key={i} className="grid grid-cols-[1fr_1.4fr_auto] gap-2">
            <input value={h.k} onChange={(e) => patch((x) => { x.about.highlights[i].k = e.target.value; })} placeholder="Label" className={inputCls} />
            <input value={h.v} onChange={(e) => patch((x) => { x.about.highlights[i].v = e.target.value; })} placeholder="Value" className={inputCls} />
            <button onClick={() => patch((x) => { x.about.highlights.splice(i, 1); })} className="border border-gold/20 px-2 text-softgrey hover:text-red-400"><Trash2 size={14} /></button>
          </div>
        ))}
        <button onClick={() => patch((x) => { (x.about.highlights = x.about.highlights || []).push({ k: "", v: "" }); })} className="flex items-center gap-1 font-sans text-xs text-gold"><Plus size={13} /> Add highlight</button>
      </>)}
    </div>
  );
}

function SettingsTab() {
  const { settings, refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sigPerson, setSigPerson] = useState("company");
  const [metrics, setMetrics] = useState(null);
  useEffect(() => { if (settings) setS(JSON.parse(JSON.stringify(settings))); }, [settings]);
  useEffect(() => { api.get("/metrics").then(({ data }) => setMetrics(data)).catch(() => {}); }, []);
  if (!s) return <div className="font-serif text-gold">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { contact: s.contact, fees_text: s.fees_text, bespoke_deposit: s.bespoke_deposit || "", what_we_source: s.what_we_source, hero: s.hero, sections: s.sections, legal: s.legal || {}, team: s.team || {}, stats: s.stats || [], stats_coming_soon: s.stats_coming_soon || false, stats_eyebrow: s.stats_eyebrow || "", stats_heading: s.stats_heading || "", process: s.process || {}, content: s.content || {}, signature: s.signature || {}, affiliations: s.affiliations || {} });
      toast.success("Settings saved"); refresh();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const setCard = (i, k, v) => { const n = [...s.what_we_source]; n[i] = { ...n[i], [k]: v }; setS({ ...s, what_we_source: n }); };
  const setStat = (i, k, v) => { const st = [...(s.stats || [])]; st[i] = { ...st[i], [k]: v }; setS({ ...s, stats: st }); };
  const setMember = (i, k, v) => { const m = [...(s.team?.members || [])]; m[i] = { ...m[i], [k]: v }; setS({ ...s, team: { ...s.team, members: m } }); };
  const setLegal = (k, v) => setS({ ...s, legal: { ...s.legal, [k]: v } });
  const setProc = (k, v) => setS({ ...s, process: { ...s.process, [k]: v } });
  const uploadTeamImg = async (i, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setMember(i, "image", data.url); toast.success("Image uploaded"); }
    catch { toast.error("Upload failed"); }
  };
  const setSig = (k, v) => setS({ ...s, signature: { ...s.signature, [k]: v } });
  const setAff = (k, v) => setS({ ...s, affiliations: { ...s.affiliations, [k]: v } });
  const setLogo = (i, k, v) => { const l = [...(s.affiliations?.logos || [])]; l[i] = { ...l[i], [k]: v }; setS({ ...s, affiliations: { ...s.affiliations, logos: l } }); };
  const uploadLogo = async (i, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setLogo(i, "image", data.url); toast.success("Logo uploaded"); }
    catch { toast.error("Upload failed"); }
  };
  const uploadSigLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setSig("logo", data.url); toast.success("Logo uploaded"); }
    catch { toast.error("Upload failed"); }
  };
  const sig = s.signature || {};
  const _members = s.team?.members || [];
  const _person = sigPerson === "company" ? null : _members[Number(sigPerson)];
  const _name = _person ? _person.name : (sig.name || "");
  const _title = _person ? _person.role : (sig.title || "");
  const _companyLogo = sig.logo ? fileUrl(sig.logo) : "";
  const _memberAvatar = _person && _person.image ? fileUrl(_person.image) : "";
  const _whats = sig.whatsapp || s.contact?.whatsapp || "";
  const _rows = [
    sig.email && `<a href="mailto:${sig.email}" style="color:#082C3A;text-decoration:none">✉&nbsp; ${sig.email}</a>`,
    sig.phone && `☎&nbsp; ${sig.phone}`,
    _whats && `<a href="https://wa.me/${_whats.replace(/[^0-9]/g, "")}" style="color:#25D366;text-decoration:none">✆&nbsp; ${_whats}</a>`,
    sig.website && `🔗&nbsp; ${sig.website}`,
    sig.linkedin && `<a href="${sig.linkedin}" style="color:#082C3A;text-decoration:none">in&nbsp; LinkedIn</a>`,
  ].filter(Boolean).map((r) => `<div style="margin:4px 0">${r}</div>`).join("");
  const _affLogos = (sig.show_affiliations !== false ? (s.affiliations?.logos || []) : []).filter((l) => l.image);
  const _logoCell = _companyLogo
    ? `<td style="padding-right:20px;border-right:3px solid #C9A227;vertical-align:middle"><img src="${_companyLogo}" alt="${sig.company || ""}" style="max-height:66px;max-width:150px"/></td>`
    : `<td style="padding-right:20px;border-right:3px solid #C9A227;vertical-align:middle"><div style="font-family:Georgia,serif;font-size:20px;color:#C9A227;white-space:nowrap">${sig.company || "Goldstone Sourcing"}</div></td>`;
  const _avatarCell = _memberAvatar
    ? `<td style="padding-left:20px;padding-right:14px;vertical-align:middle"><img src="${_memberAvatar}" alt="${_name}" style="width:58px;height:58px;border-radius:50%;object-fit:cover;display:block"/></td>`
    : "";
  const _textPad = _memberAvatar ? "0" : "20px";
  const _affRow = _affLogos.length
    ? `<tr><td colspan="3" style="padding-top:16px"><div style="font-size:9px;letter-spacing:2px;text-transform:uppercase;color:#A7ADB2;margin-bottom:8px">In Association With</div>${_affLogos.map((l) => `<img src="${fileUrl(l.image)}" alt="${l.name}" style="max-height:28px;margin-right:18px;vertical-align:middle"/>`).join("")}</td></tr>`
    : "";
  const sigHtml = `<table cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif;color:#080A0B"><tr>${_logoCell}${_avatarCell}<td style="padding-left:${_textPad};font-size:12px;color:#444;vertical-align:middle"><div style="font-size:17px;font-weight:bold;color:#080A0B">${_name}</div><div style="font-size:12px;color:#082C3A;margin:4px 0 10px">${_title}${sig.company ? " · " + sig.company : ""}</div>${sig.tagline ? `<div style="color:#C9A227;font-style:italic;margin-bottom:10px">${sig.tagline}</div>` : ""}${_rows}</td></tr>${_affRow}</table>`;
  const copySignatureRich = async () => {
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([sigHtml], { type: "text/html" }),
          "text/plain": new Blob([sigHtml], { type: "text/plain" }),
        });
        await navigator.clipboard.write([item]);
        toast.success("Signature copied — paste into Outlook (Ctrl/Cmd+V)");
        return;
      }
      throw new Error("no-clipboarditem");
    } catch {
      const holder = document.createElement("div");
      holder.setAttribute("contenteditable", "true");
      holder.style.position = "fixed";
      holder.style.left = "-9999px";
      holder.innerHTML = sigHtml;
      document.body.appendChild(holder);
      const range = document.createRange();
      range.selectNodeContents(holder);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      try {
        const ok = document.execCommand("copy");
        toast[ok ? "success" : "error"](ok ? "Signature copied — paste into Outlook (Ctrl/Cmd+V)" : "Copy failed — select the preview and copy manually");
      } catch {
        toast.error("Copy failed — select the preview and copy manually");
      }
      selection.removeAllRanges();
      document.body.removeChild(holder);
    }
  };


  return (
    <div className="max-w-3xl space-y-10" data-testid="settings-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl text-warmwhite">Site Settings</h2>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-gold px-5 py-2.5 font-sans text-sm font-medium text-obsidian hover:brightness-110 disabled:opacity-60" data-testid="save-settings"><Save size={15} /> {saving ? "Saving…" : "Save Changes"}</button>
      </div>

      <section>
        <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Hero</h3>
        <div className="mt-4 space-y-4">
          <div><div className={label}>Headline</div><input value={s.hero.headline} onChange={(e) => setS({ ...s, hero: { ...s.hero, headline: e.target.value } })} className={`${inputCls} mt-2`} /></div>
          <div><div className={label}>Supporting Statement</div><textarea rows={2} value={s.hero.sub} onChange={(e) => setS({ ...s, hero: { ...s.hero, sub: e.target.value } })} className={`${inputCls} mt-2`} /></div>
        </div>
      </section>

      <section>
        <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Contact Details</h3>
        <div className="mt-4 grid grid-cols-2 gap-4">
          {[["email", "Email"], ["phone", "Phone"], ["whatsapp", "WhatsApp"], ["address", "Address"], ["linkedin", "LinkedIn URL"], ["company_registration", "Company Registration"]].map(([k, l]) => (
            <div key={k}><div className={label}>{l}</div><input value={s.contact[k] || ""} onChange={(e) => setS({ ...s, contact: { ...s.contact, [k]: e.target.value } })} className={`${inputCls} mt-2`} data-testid={`setting-${k}`} /></div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Bespoke Fees Statement</h3>
        <textarea rows={3} value={s.fees_text} onChange={(e) => setS({ ...s, fees_text: e.target.value })} className={`${inputCls} mt-4`} />
      </section>

      <section>
        <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">What We Source Cards</h3>
        <div className="mt-4 space-y-4">
          {s.what_we_source.map((card, i) => (
            <div key={card.key} className={`border p-4 ${card.hidden ? "border-gold/10 opacity-50" : "border-gold/20"}`} data-testid={`wws-card-${card.key}`}>
              <div className="flex items-center justify-between">
                <input value={card.title} onChange={(e) => setCard(i, "title", e.target.value)} className={`${inputCls} max-w-xs`} />
                <div className="flex gap-2">
                  <button onClick={() => setCard(i, "hidden", !card.hidden)} className="text-softgrey hover:text-gold" title={card.hidden ? "Show" : "Hide"} data-testid={`wws-toggle-${card.key}`}>{card.hidden ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                  <button onClick={() => setS({ ...s, what_we_source: s.what_we_source.filter((_, j) => j !== i) })} className="text-softgrey hover:text-red-400" title="Delete" data-testid={`wws-delete-${card.key}`}><Trash2 size={16} /></button>
                </div>
              </div>
              <textarea rows={2} value={card.body} onChange={(e) => setCard(i, "body", e.target.value)} className={`${inputCls} mt-3`} />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <input value={card.cta} onChange={(e) => setCard(i, "cta", e.target.value)} placeholder="CTA label" className={inputCls} />
                <input value={card.link} onChange={(e) => setCard(i, "link", e.target.value)} placeholder="Link" className={inputCls} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="stats-admin-section">
        <div className="flex items-center justify-between">
          <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">"By The Numbers" — Statistics Section</h3>
          <label className="flex cursor-pointer items-center gap-2 font-sans text-xs text-softgrey" data-testid="stats-coming-soon-toggle">
            <input type="checkbox" checked={!!s.stats_coming_soon} onChange={(e) => setS({ ...s, stats_coming_soon: e.target.checked })} />
            Show "Coming Soon" (hide figures)
          </label>
        </div>
        {s.stats_coming_soon && <p className="mt-2 font-sans text-[11px] text-champagne">Figures are hidden on the site and replaced with a "Coming Soon" panel. Uncheck to publish them once verified.</p>}
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label className={label}>Eyebrow / small heading</label>
            <input value={s.stats_eyebrow ?? ""} onChange={(e) => setS({ ...s, stats_eyebrow: e.target.value })} placeholder="By The Numbers" className={`${inputCls} mt-1`} data-testid="stats-eyebrow-input" />
          </div>
          <div>
            <label className={label}>Main heading</label>
            <input value={s.stats_heading ?? ""} onChange={(e) => setS({ ...s, stats_heading: e.target.value })} placeholder="Measured, discreet, deliberate." className={`${inputCls} mt-1`} data-testid="stats-heading-input" />
          </div>
        </div>
        <div className={`mt-4 space-y-3 ${s.stats_coming_soon ? "opacity-40" : ""}`} data-testid="stats-editor">
          {(s.stats || []).map((st, i) => {
            const isLive = st.source && st.source !== "manual";
            const liveVal = isLive && metrics ? metrics[st.source] : null;
            return (
              <div key={i} className="border border-gold/15 p-3">
                <div className="grid grid-cols-[1.4fr_auto] gap-3">
                  <select value={st.source || "manual"} onChange={(e) => setStat(i, "source", e.target.value)} className={`${inputCls} [&>option]:bg-obsidian`} data-testid={`stat-source-${i}`}>
                    {STAT_SOURCES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                  <button onClick={() => setS({ ...s, stats: s.stats.filter((_, j) => j !== i) })} className="border border-gold/20 px-3 text-softgrey hover:text-red-400" title="Delete"><Trash2 size={14} /></button>
                </div>
                <div className="mt-3 grid grid-cols-[1fr_0.6fr_2fr] gap-3">
                  <input value={isLive ? (liveVal != null ? String(liveVal) : "…") : st.value} onChange={(e) => setStat(i, "value", e.target.value)} disabled={isLive} placeholder="Value" className={`${inputCls} ${isLive ? "cursor-not-allowed text-gold/70" : ""}`} data-testid={`stat-value-${i}`} />
                  <input value={st.suffix || ""} onChange={(e) => setStat(i, "suffix", e.target.value)} placeholder="Suffix" className={inputCls} />
                  <input value={st.label} onChange={(e) => setStat(i, "label", e.target.value)} placeholder="Label" className={inputCls} />
                </div>
                {isLive && <p className="mt-2 font-sans text-[10px] text-gold/70">Auto-generated from CRM/opportunities{liveVal != null ? ` — current: ${liveVal}${st.suffix || ""}` : ""}. Updates automatically.</p>}
              </div>
            );
          })}
          <button onClick={() => setS({ ...s, stats: [...(s.stats || []), { value: "0", suffix: "", label: "New Stat", source: "manual" }] })} className="flex items-center gap-1 font-sans text-xs text-gold"><Plus size={13} /> Add Stat</button>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between">
          <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Our People / Team</h3>
          <label className="flex items-center gap-2 font-sans text-xs text-softgrey" data-testid="team-hidden-toggle">
            <input type="checkbox" checked={!s.team?.hidden} onChange={(e) => setS({ ...s, team: { ...s.team, hidden: !e.target.checked }, sections: { ...s.sections, team: e.target.checked } })} /> Show section
          </label>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div><div className={label}>Heading</div><input value={s.team?.heading || ""} onChange={(e) => setS({ ...s, team: { ...s.team, heading: e.target.value } })} className={`${inputCls} mt-2`} /></div>
          <div><div className={label}>Subheading</div><input value={s.team?.subheading || ""} onChange={(e) => setS({ ...s, team: { ...s.team, subheading: e.target.value } })} className={`${inputCls} mt-2`} /></div>
        </div>
        <div className="mt-4 space-y-4" data-testid="team-editor">
          {(s.team?.members || []).map((m, i) => (
            <div key={i} className="border border-gold/15 p-4">
              <div className="flex items-start gap-4">
                <label className="flex h-20 w-20 shrink-0 cursor-pointer items-center justify-center overflow-hidden border border-dashed border-gold/30 text-gold">
                  {m.image ? <img src={fileUrl(m.image)} alt="" className="h-full w-full object-cover" /> : <Plus size={16} />}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadTeamImg(i, e)} />
                </label>
                <div className="flex-1 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input value={m.name || ""} onChange={(e) => setMember(i, "name", e.target.value)} placeholder="Name" className={inputCls} />
                    <input value={m.role || ""} onChange={(e) => setMember(i, "role", e.target.value)} placeholder="Role" className={inputCls} />
                  </div>
                  <textarea rows={2} value={m.bio || ""} onChange={(e) => setMember(i, "bio", e.target.value)} placeholder="Short bio" className={inputCls} />
                  <input value={m.linkedin || ""} onChange={(e) => setMember(i, "linkedin", e.target.value)} placeholder="LinkedIn URL (optional)" className={inputCls} />
                  <input value={m.image || ""} onChange={(e) => setMember(i, "image", e.target.value)} placeholder="Or paste photo URL (e.g. Cloudinary)" className={inputCls} data-testid={`team-image-url-${i}`} />
                </div>
                <div className="flex flex-col items-center gap-3">
                  <label className="flex items-center gap-1 font-sans text-[10px] text-softgrey" data-testid={`team-hide-${i}`}>
                    <input type="checkbox" checked={!m.hidden} onChange={(e) => setMember(i, "hidden", !e.target.checked)} /> Show
                  </label>
                  <button onClick={() => setS({ ...s, team: { ...s.team, members: s.team.members.filter((_, j) => j !== i) } })} className="text-softgrey hover:text-red-400"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
          <button onClick={() => setS({ ...s, team: { ...s.team, members: [...(s.team?.members || []), { name: "", role: "", bio: "", linkedin: "", image: "" }] } })} className="flex items-center gap-1 font-sans text-xs text-gold"><Plus size={13} /> Add Team Member</button>
        </div>
      </section>

      <section>
        <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Bespoke Process</h3>
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div><div className={label}>Deposit</div><input value={s.process?.deposit || ""} onChange={(e) => setProc("deposit", e.target.value)} className={`${inputCls} mt-2`} /></div>
          <div><div className={label}>Deposit Note</div><input value={s.process?.deposit_note || ""} onChange={(e) => setProc("deposit_note", e.target.value)} className={`${inputCls} mt-2`} /></div>
          <div><div className={label}>Search Window</div><input value={s.process?.window || ""} onChange={(e) => setProc("window", e.target.value)} className={`${inputCls} mt-2`} /></div>
        </div>
        <div className="mt-4"><div className={label}>Refund Guarantee</div><textarea rows={2} value={s.process?.refund || ""} onChange={(e) => setProc("refund", e.target.value)} className={`${inputCls} mt-2`} /></div>
      </section>

      <section>
        <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Legal Documents</h3>
        <div className="mt-4 space-y-4" data-testid="legal-editor">
          {[["privacy", "Privacy Policy"], ["terms", "Terms of Service"], ["complaints", "Complaints Procedure"]].map(([k, l]) => (
            <div key={k}>
              <div className={label}>{l}</div>
              <textarea rows={6} value={s.legal?.[k] || ""} onChange={(e) => setLegal(k, e.target.value)} className={`${inputCls} mt-2 font-mono text-xs`} data-testid={`legal-${k}`} />
            </div>
          ))}
          <p className="font-sans text-[11px] text-softgrey/60">Use ## for a main heading, ### for a sub-heading, and blank lines between paragraphs.</p>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between">
          <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Affiliations (Moving Logos)</h3>
          <label className="flex items-center gap-2 font-sans text-xs text-softgrey" data-testid="affiliations-show">
            <input type="checkbox" checked={!s.affiliations?.hidden} onChange={(e) => { setAff("hidden", !e.target.checked); setS((p) => ({ ...p, sections: { ...p.sections, affiliations: e.target.checked } })); }} /> Show section
          </label>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div><div className={label}>Heading</div><input value={s.affiliations?.heading || ""} onChange={(e) => setAff("heading", e.target.value)} className={`${inputCls} mt-2`} /></div>
          <div><div className={label}>Subheading</div><input value={s.affiliations?.subheading || ""} onChange={(e) => setAff("subheading", e.target.value)} className={`${inputCls} mt-2`} /></div>
        </div>
        <div className="mt-4 space-y-3" data-testid="affiliations-editor">
          {(s.affiliations?.logos || []).map((lg, i) => (
            <div key={i} className="flex items-center gap-3 border border-gold/15 p-3">
              <label className="flex h-14 w-20 shrink-0 cursor-pointer items-center justify-center overflow-hidden border border-dashed border-gold/30 text-gold">
                {lg.image ? <img src={fileUrl(lg.image)} alt="" className="h-full w-full object-contain" /> : <Plus size={14} />}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(i, e)} />
              </label>
              <div className="flex-1 space-y-2">
                <input value={lg.name || ""} onChange={(e) => setLogo(i, "name", e.target.value)} placeholder="Organisation name" className={inputCls} />
                <input value={lg.image || ""} onChange={(e) => setLogo(i, "image", e.target.value)} placeholder="Or paste logo URL (Cloudinary)" className={inputCls} data-testid={`affiliation-url-${i}`} />
              </div>
              <button onClick={() => setAff("logos", s.affiliations.logos.filter((_, j) => j !== i))} className="text-softgrey hover:text-red-400"><Trash2 size={15} /></button>
            </div>
          ))}
          <button onClick={() => setAff("logos", [...(s.affiliations?.logos || []), { name: "", image: "" }])} className="flex items-center gap-1 font-sans text-xs text-gold" data-testid="add-affiliation"><Plus size={13} /> Add Logo</button>
        </div>
      </section>

      <section>
        <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Email Signature</h3>
        <div className="mt-4">
          <div className={label}>Signature For</div>
          <select value={sigPerson} onChange={(e) => setSigPerson(e.target.value)} className={`${inputCls} mt-2 [&>option]:bg-obsidian`} data-testid="sig-person">
            <option value="company">Company (default fields below)</option>
            {_members.map((m, i) => <option key={i} value={String(i)}>{m.name || `Member ${i + 1}`} — {m.role}</option>)}
          </select>
          <p className="mt-2 font-sans text-[11px] text-softgrey/60">Pick a team member to auto-fill their name, role and photo, or "Company" to use the fields below.</p>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          {[["name", "Name"], ["title", "Title"], ["company", "Company"], ["tagline", "Tagline"], ["email", "Email"], ["phone", "Phone"], ["whatsapp", "WhatsApp"], ["website", "Website"], ["linkedin", "LinkedIn URL"]].map(([k, l]) => (
            <div key={k}><div className={label}>{l}</div><input value={sig[k] || ""} onChange={(e) => setSig(k, e.target.value)} className={`${inputCls} mt-2`} data-testid={`sig-${k}`} /></div>
          ))}
        </div>
        <div className="mt-4 flex items-end gap-3">
          <div className="flex-1"><div className={label}>Company Logo URL</div><input value={sig.logo || ""} onChange={(e) => setSig("logo", e.target.value)} placeholder="Paste logo URL (Cloudinary)" className={`${inputCls} mt-2`} data-testid="sig-logo" /></div>
          <label className="flex h-[42px] cursor-pointer items-center gap-2 border border-dashed border-gold/30 px-4 font-sans text-xs text-gold"><Upload size={14} /> Upload<input type="file" accept="image/*" className="hidden" onChange={uploadSigLogo} /></label>
        </div>
        <label className="mt-4 flex items-center gap-2 font-sans text-sm text-softgrey" data-testid="sig-affiliations-toggle">
          <input type="checkbox" checked={sig.show_affiliations !== false} onChange={(e) => setSig("show_affiliations", e.target.checked)} /> Include affiliate logos in signature
        </label>
        <div className="mt-5">
          <div className={label}>Preview</div>
          <div className="mt-2 rounded border border-gold/15 bg-warmwhite p-5" data-testid="signature-preview" dangerouslySetInnerHTML={{ __html: sigHtml }} />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button onClick={copySignatureRich} className="flex items-center gap-2 border border-gold/60 bg-gold/10 px-5 py-2.5 font-sans text-sm text-warmwhite hover:bg-gold/20" data-testid="copy-signature"><Save size={14} className="text-gold" /> Copy Signature (for Outlook / Gmail)</button>
          <button onClick={() => { navigator.clipboard.writeText(sigHtml); toast.success("Raw HTML source copied"); }} className="flex items-center gap-2 border border-gold/25 px-5 py-2.5 font-sans text-sm text-softgrey hover:bg-gold/5" data-testid="copy-signature-source"><Save size={14} className="text-softgrey" /> Copy HTML Source</button>
        </div>
        <p className="mt-2 font-sans text-[11px] text-softgrey/60">Use <span className="text-gold">Copy Signature</span> to paste the rendered card directly into Outlook, Gmail or Apple Mail. Use <span className="text-softgrey">Copy HTML Source</span> only if your mail client asks for raw HTML code.</p>
      </section>

      <section>
        <h3 className="font-sans text-[11px] tracking-luxe uppercase text-gold">Homepage Section Visibility</h3>
        <div className="mt-4 grid grid-cols-2 gap-3">
          {Object.keys(s.sections).map((k) => (
            <label key={k} className="flex items-center gap-2 border border-gold/15 px-4 py-3 font-sans text-sm capitalize text-softgrey" data-testid={`section-toggle-${k}`}>
              <input type="checkbox" checked={s.sections[k]} onChange={(e) => setS({ ...s, sections: { ...s.sections, [k]: e.target.checked } })} /> {k.replace(/_/g, " ")}
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}
